import axios from 'axios'

/**
 * DRF връща или `{"detail": "..."}`, или речник с грешки по поле. Екраните
 * показват едно изречение на български — разгъването става тук, за да не се
 * повтаря във всеки компонент.
 */
export function errorMessage(error: unknown, fallback = 'Възникна грешка. Опитайте отново.'): string {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error && error.message ? error.message : fallback
  }

  if (error.code === 'ERR_NETWORK') {
    return 'Няма връзка със сървъра.'
  }

  const data = error.response?.data
  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }
  if (data && typeof data === 'object') {
    const detail = (data as Record<string, unknown>).detail
    if (typeof detail === 'string') {
      return detail
    }
    const first = fieldErrors(data as Record<string, unknown>)[0]
    if (first) {
      return first
    }
  }

  if (error.response?.status === 500) {
    return 'Сървърна грешка. Данните не са записани.'
  }
  return fallback
}

/** Полетата на редовете (фактура, сканиране, наряд) — за да каже грешката кое. */
const LINE_FIELDS: Record<string, string> = {
  description: 'Описание',
  quantity: 'Количество',
  unit_price: 'Ед. цена',
  amount: 'Основа',
  vat_rate: 'ДДС %',
  vat_basis: 'Основание без ДДС',
  unit: 'Мярка',
  project: 'Обект',
  cost_centre: 'Разходен център',
  category: 'Категория',
}

/**
 * Всички съобщения за грешка, сплескани до плосък списък. Грешка в ред от
 * таблица казва кой ред и кое поле: „Ред 2 · Количество: …“.
 */
export function fieldErrors(data: Record<string, unknown>): string[] {
  const messages: string[] = []
  const walk = (value: unknown, where: string) => {
    if (typeof value === 'string') {
      messages.push(where ? `${where}: ${value}` : value)
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const isRow = item && typeof item === 'object' && !Array.isArray(item)
        walk(item, isRow ? `Ред ${index + 1}` : where)
      })
    } else if (value && typeof value === 'object') {
      for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
        const label = where && LINE_FIELDS[key]
        walk(item, label ? `${where} · ${label}` : where)
      }
    }
  }
  walk(data, '')
  return messages
}

/** Грешки по поле, ключирани както ги връща сериализаторът. */
export function errorsByField(error: unknown): Record<string, string> {
  if (!axios.isAxiosError(error)) {
    return {}
  }
  const data = error.response?.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {}
  }
  const result: Record<string, string> = {}
  for (const [field, value] of Object.entries(data as Record<string, unknown>)) {
    if (field === 'detail') {
      continue
    }
    const [first] = fieldErrors({ value })
    if (first) {
      result[field] = first
    }
  }
  return result
}
